import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Building2,
  CheckCircle2,
  ChevronRight,
  FileCheck2,
  Flame,
  Loader2,
  MapPin,
  MessageCircle,
  Play,
  Quote,
  ShieldCheck,
  Star,
  Target,
  Truck,
  User,
} from 'lucide-react';
import SiteFooter from '../components/SiteFooter';
import BrandTrustStrip from '../components/BrandTrustStrip';
import { CINTA_LANDING, CINTA_WA_E164 } from '../content/cintaLandingCopy';
import {
  checkoutCintaCart,
  CINTA_CART_STORAGE_KEY,
  fetchCintaProduct,
  formatCep,
  formatCpfCnpj,
  formatPhoneDisplay,
  lookupCep,
  normalizePhoneDigits,
  upsertCintaCart,
  type CintaProduct,
} from '../lib/cintaApi';
import { trackPublicEvent } from '../lib/publicAnalytics';
import {
  buildWhatsappHref,
  initMetaPixel,
  pixelTrack,
} from '../lib/metaPixel';

const TRUST_ICON_MAP: Record<string, typeof ShieldCheck> = {
  receipt: FileCheck2,
  shield: ShieldCheck,
  truck: Truck,
  building: Building2,
};

const HOW_ICON_MAP: Record<string, typeof Activity> = {
  vibration: Activity,
  heat: Flame,
  body: Target,
};

type CheckoutStep = 1 | 2 | 3;

function scrollToCheckout() {
  document.getElementById('checkout')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function CintaLanding() {
  const [product, setProduct] = useState<CintaProduct | null>(null);
  const [paymentReturn, setPaymentReturn] = useState<'approved' | 'pending' | 'failed' | null>(null);
  const [step, setStep] = useState<CheckoutStep>(1);
  const [cartId, setCartId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [cep, setCep] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [bairro, setBairro] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [uf, setUf] = useState('');

  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const [heroVideoFailed, setHeroVideoFailed] = useState(false);

  useEffect(() => {
    document.title = CINTA_LANDING.pageTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', CINTA_LANDING.metaDescription);
    initMetaPixel();
    trackPublicEvent('cinta_view', { page: '/cinta' });
    pixelTrack('ViewContent', { content_name: 'Cinta Modeladora', content_category: 'cinta' });

    // Retorno das back_urls do Mercado Pago (?paid=1|0|pending)
    const paid = new URLSearchParams(window.location.search).get('paid');
    if (paid === '1') {
      setPaymentReturn('approved');
      window.localStorage.removeItem(CINTA_CART_STORAGE_KEY);
      trackPublicEvent('cinta_payment_return', { status: 'approved' });
    } else if (paid === 'pending') {
      setPaymentReturn('pending');
      trackPublicEvent('cinta_payment_return', { status: 'pending' });
    } else if (paid === '0') {
      setPaymentReturn('failed');
      trackPublicEvent('cinta_payment_return', { status: 'failed' });
    }

    const stored = paid === '1' ? null : window.localStorage.getItem(CINTA_CART_STORAGE_KEY);
    if (stored) setCartId(stored);

    fetchCintaProduct()
      .then(setProduct)
      .catch(() => {
        setProduct({
          name: 'Cinta Modeladora',
          priceFullCents: 198_000,
          priceActionCents: 118_800,
          installmentCount: 12,
          installmentValueCents: 9_900,
          priceFullLabel: 'R$ 1.980,00',
          priceActionLabel: 'R$ 1.188,00',
          installmentLabel: '12x R$ 99,00',
          campaign: 'Campanha Bem-Estar Lunins',
          benefits: [],
        });
      });
  }, []);

  useEffect(() => {
    if (name && !razaoSocial) setRazaoSocial(name);
  }, [name, razaoSocial]);

  const waLink = useMemo(
    () => buildWhatsappHref(CINTA_WA_E164, CINTA_LANDING.whatsappPrefill),
    [],
  );

  const saveStep = useCallback(
    async (nextStep: CheckoutStep, extra?: Parameters<typeof upsertCintaCart>[0]) => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          cartId: cartId ?? undefined,
          name: name.trim() || undefined,
          phone: phone ? normalizePhoneDigits(phone) : undefined,
          email: email.trim() || undefined,
          step: (nextStep === 1 ? 'contact' : nextStep === 2 ? 'fiscal' : 'review') as
            | 'contact'
            | 'fiscal'
            | 'review',
          fiscal: {
            cpfCnpj: cpfCnpj.replace(/\D/g, '') || undefined,
            razaoSocial: razaoSocial.trim() || undefined,
            cep: cep.replace(/\D/g, '') || undefined,
            logradouro: logradouro.trim() || undefined,
            numero: numero.trim() || undefined,
            bairro: bairro.trim() || undefined,
            municipio: municipio.trim() || undefined,
            uf: uf.trim().toUpperCase() || undefined,
            email: email.trim() || undefined,
          },
          ...extra,
        };
        const result = await upsertCintaCart(payload);
        setCartId(result.cartId);
        setStep(nextStep);
        trackPublicEvent(`cinta_cart_step_${nextStep}` as 'cinta_cart_step_1', {
          cartId: result.cartId,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao salvar dados');
      } finally {
        setLoading(false);
      }
    },
    [cartId, name, phone, email, cpfCnpj, razaoSocial, cep, logradouro, numero, bairro, municipio, uf],
  );

  async function handleCepBlur() {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    setError(null);
    try {
      const data = await lookupCep(digits);
      setLogradouro(data.logradouro || logradouro);
      setBairro(data.bairro || bairro);
      setMunicipio(data.municipio || municipio);
      setUf(data.uf || uf);
    } catch {
      setError('CEP não encontrado — preencha o endereço manualmente.');
    } finally {
      setCepLoading(false);
    }
  }

  async function handleCheckout() {
    if (!cartId) {
      setError('Salve seus dados nas etapas anteriores.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await saveStep(3);
      const fiscal = {
        cpfCnpj: cpfCnpj.replace(/\D/g, ''),
        razaoSocial: razaoSocial.trim(),
        cep: cep.replace(/\D/g, ''),
        logradouro: logradouro.trim(),
        numero: numero.trim(),
        bairro: bairro.trim(),
        municipio: municipio.trim(),
        uf: uf.trim().toUpperCase().slice(0, 2),
        email: email.trim() || undefined,
      };
      const result = await checkoutCintaCart({ cartId, fiscal });
      trackPublicEvent('cinta_checkout_redirect', { cartId, stub: Boolean(result.stub) });
      pixelTrack('InitiateCheckout', {
        content_name: 'Cinta Modeladora',
        value: (product?.priceActionCents ?? 118_800) / 100,
        currency: 'BRL',
      });
      window.location.href = result.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao iniciar pagamento');
      setLoading(false);
    }
  }

  function handleCta(source: string) {
    trackPublicEvent('cinta_cta_click', { source });
    scrollToCheckout();
  }

  function handleWhatsappClick(source: string) {
    trackPublicEvent('cinta_whatsapp_click', { source });
    pixelTrack('Contact', { content_name: 'Cinta Modeladora', source });
  }

  const installmentLabel = product?.installmentLabel ?? CINTA_LANDING.hero.installment;
  const anchorFull = product?.priceFullLabel ?? CINTA_LANDING.hero.anchorFull;

  return (
    <div className="flex min-h-screen flex-col bg-lunins-cream">
      <header className="sticky top-0 z-40 border-b border-lunins-border/60 bg-lunins-cream backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <div className="font-display text-lg font-bold text-lunins-ink">
            Lunins <span className="text-lunins-accent">·</span> Cinta
          </div>
          <div className="hidden text-sm text-lunins-muted sm:block">{installmentLabel} · frete grátis</div>
          <div className="flex gap-2">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleWhatsappClick('header')}
              className="inline-flex items-center gap-1.5 rounded-full border border-lunins-border px-3 py-2 text-xs font-semibold text-lunins-ink hover:border-lunins-accent/40"
            >
              <MessageCircle className="h-4 w-4 text-lunins-accent" />
              WhatsApp
            </a>
            <button
              type="button"
              onClick={() => handleCta('header')}
              className="rounded-full bg-lunins-ink px-4 py-2 text-xs font-semibold text-lunins-cream hover:bg-lunins-ink-soft"
            >
              Comprar
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Retorno do pagamento Mercado Pago */}
        {paymentReturn && (
          <section className="mx-auto max-w-6xl px-4 pt-4">
            {paymentReturn === 'approved' && (
              <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                <div className="text-sm text-emerald-100">
                  <strong className="font-semibold text-lunins-ink">Pagamento aprovado!</strong> Sua nota fiscal e o
                  código de rastreio chegam pelo WhatsApp informado na compra. Obrigado pela confiança!
                </div>
              </div>
            )}
            {paymentReturn === 'pending' && (
              <div className="flex items-start gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4">
                <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-amber-400" />
                <div className="text-sm text-amber-100">
                  <strong className="font-semibold text-lunins-ink">Pagamento em processamento.</strong> Assim que o
                  Mercado Pago confirmar, avisamos pelo WhatsApp com a nota fiscal e o rastreio.
                </div>
              </div>
            )}
            {paymentReturn === 'failed' && (
              <div className="flex items-start gap-3 rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-rose-400" />
                <div className="text-sm text-rose-100">
                  <strong className="font-semibold text-lunins-ink">O pagamento não foi concluído.</strong> Nenhum valor
                  foi cobrado — você pode{' '}
                  <button type="button" onClick={() => handleCta('payment_failed')} className="font-semibold underline">
                    tentar novamente
                  </button>{' '}
                  ou{' '}
                  <a href={waLink} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
                    falar com a gente no WhatsApp
                  </a>
                  .
                </div>
              </div>
            )}
          </section>
        )}

        {/* Hero */}
        <section className="relative overflow-hidden bg-hero-mesh pb-12 pt-8 md:pb-16 md:pt-12">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 lg:grid-cols-2 lg:items-center">
            <div className="animate-fade-up">
              <p className="text-xs font-semibold uppercase tracking-widest text-lunins-accent">
                {CINTA_LANDING.hero.eyebrow}
              </p>
              <h1 className="mt-3 font-display text-3xl font-bold leading-tight text-lunins-ink sm:text-4xl lg:text-5xl">
                {CINTA_LANDING.hero.headline}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-lunins-ink/80 sm:text-lg">
                {CINTA_LANDING.hero.subheadline}
              </p>
              <div className="mt-6 flex flex-wrap items-end gap-3">
                <div>
                  <div className="text-sm text-lunins-muted line-through">De {anchorFull}</div>
                  <div className="font-display text-3xl font-bold text-lunins-accent">{installmentLabel}</div>
                  <div className="text-xs text-lunins-muted">no cartão · Campanha Bem-Estar</div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => handleCta('hero')}
                  className="inline-flex items-center gap-2 rounded-full bg-lunins-ink px-6 py-3 text-sm font-semibold text-lunins-cream shadow-glow hover:bg-lunins-ink-soft"
                >
                  {CINTA_LANDING.hero.cta}
                  <ChevronRight className="h-4 w-4" />
                </button>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleWhatsappClick('hero')}
                  className="inline-flex items-center gap-2 rounded-full border border-lunins-border px-5 py-3 text-sm font-semibold text-lunins-ink hover:border-lunins-accent/40"
                >
                  <MessageCircle className="h-4 w-4" />
                  Tirar dúvidas
                </a>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-3xl border border-lunins-border/80 bg-lunins-sand shadow-card">
              {!heroVideoFailed ? (
                <video
                  ref={heroVideoRef}
                  className="aspect-[9/16] max-h-[70vh] w-full object-cover sm:aspect-video sm:max-h-none"
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={CINTA_LANDING.heroPoster}
                  onError={() => setHeroVideoFailed(true)}
                >
                  <source src={CINTA_LANDING.heroVideo} type="video/mp4" />
                </video>
              ) : (
                <img
                  src={CINTA_LANDING.catalogImage}
                  alt="Cinta Modeladora Lunins"
                  className="aspect-[4/5] w-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/cinta/catalogo.jpeg';
                  }}
                />
              )}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-lunins-night/60 to-transparent" />
            </div>
          </div>
        </section>

        {/* Depoimento destaque — logo após o hero para gerar confiança */}
        {CINTA_LANDING.testimonials.items[0] && (
          <section className="border-y border-lunins-accent/10 bg-gradient-to-b from-lunins-deep/80 to-lunins-night py-10">
            <div className="mx-auto max-w-4xl px-4">
              <div className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest text-lunins-accent">
                <Star className="h-3.5 w-3.5 fill-lunins-accent" />
                Depoimento real
                <Star className="h-3.5 w-3.5 fill-lunins-accent" />
              </div>
              <div className="mt-6 overflow-hidden rounded-2xl border border-lunins-border bg-lunins-surface shadow-card">
                <video
                  className="aspect-video w-full bg-black"
                  controls
                  playsInline
                  preload="metadata"
                >
                  <source src={CINTA_LANDING.testimonials.items[0].video} type="video/mp4" />
                </video>
                <div className="flex items-center gap-3 border-t border-lunins-border px-5 py-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-lunins-accent/10">
                    <User className="h-4 w-4 text-lunins-accent" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-lunins-ink">{CINTA_LANDING.testimonials.items[0].name}</div>
                    <div className="flex items-center gap-1 text-xs text-lunins-muted">
                      <MapPin className="h-3 w-3" />
                      {CINTA_LANDING.testimonials.items[0].location} · {CINTA_LANDING.testimonials.items[0].usagePeriod}
                    </div>
                  </div>
                  <Quote className="h-5 w-5 text-lunins-accent/40" />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Trust bar */}
        <section className="border-y border-lunins-border/60 bg-lunins-sand/70 py-8">
          <div className="mx-auto grid max-w-6xl gap-4 px-4 sm:grid-cols-2 lg:grid-cols-4">
            {CINTA_LANDING.trust.map((item) => {
              const Icon = TRUST_ICON_MAP[item.icon] ?? ShieldCheck;
              return (
                <div key={item.title} className="flex gap-3 rounded-2xl border border-lunins-border/50 bg-lunins-surface/40 p-4">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-lunins-accent" />
                  <div>
                    <div className="text-sm font-semibold text-lunins-ink">{item.title}</div>
                    <div className="mt-1 text-xs leading-relaxed text-lunins-muted">{item.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Como funciona */}
        <section className="py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="font-display text-2xl font-bold text-lunins-ink sm:text-3xl">Como funciona</h2>
            <p className="mt-2 max-w-2xl text-lunins-muted">
              Três mecanismos integrados para apoiar modelagem e sensação de leveza no dia a dia.
            </p>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {CINTA_LANDING.howItWorks.map((card) => {
                const Icon = HOW_ICON_MAP[card.icon] ?? Activity;
                return (
                  <div
                    key={card.title}
                    className="rounded-2xl border border-lunins-border bg-card-shine bg-lunins-surface p-6 shadow-card"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-lunins-accent/10">
                      <Icon className="h-5 w-5 text-lunins-accent" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold text-lunins-ink">{card.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-lunins-muted">{card.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Regiões */}
        <section className="border-t border-lunins-border/60 bg-lunins-sand/50 py-14">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="font-display text-2xl font-bold text-lunins-ink sm:text-3xl">Regiões de aplicação</h2>
            <p className="mt-2 max-w-2xl text-lunins-muted">Uma única cinta, cinco regiões — ajuste conforme sua necessidade.</p>
            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {CINTA_LANDING.regions.map((r) => (
                <div
                  key={r.label}
                  className="group relative overflow-hidden rounded-2xl border border-lunins-border/50 bg-lunins-surface/50 p-5 transition-all hover:border-lunins-accent/40 hover:shadow-lg hover:shadow-lunins-accent/5"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${r.gradient} opacity-0 transition-opacity group-hover:opacity-100`} />
                  <div className="relative">
                    <span className="text-4xl" role="img" aria-label={r.label}>{r.emoji}</span>
                    <h3 className="mt-3 font-display text-base font-bold text-lunins-ink">{r.label}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-lunins-muted group-hover:text-lunins-ink/80">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tutoriais Pamela */}
        <section className="border-t border-lunins-border/60 py-14 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="font-display text-2xl font-bold text-lunins-ink">Veja na prática</h2>
            <p className="mt-2 max-w-2xl text-lunins-muted">Tutoriais com a Pamela mostrando como usar a cinta e as regiões de aplicação.</p>
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              {CINTA_LANDING.tutorials.youtube.map((yt) => (
                <div key={yt.id} className="overflow-hidden rounded-2xl border border-lunins-border bg-lunins-surface shadow-card">
                  <div className="flex items-center gap-2 border-b border-lunins-border px-4 py-3 text-sm font-medium text-lunins-ink">
                    <Play className="h-4 w-4 text-lunins-accent" />
                    {yt.title}
                  </div>
                  <div className="aspect-video">
                    <iframe
                      title={yt.title}
                      src={`https://www.youtube.com/embed/${yt.id}`}
                      className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Autoridade */}
        <section className="border-y border-lunins-border/60 bg-lunins-sand/70 py-10">
          <div className="mx-auto max-w-3xl px-4 text-center">
            <h2 className="font-display text-xl font-bold text-lunins-ink">Por que confiar na Lunins</h2>
            <ul className="mt-6 space-y-3 text-sm text-lunins-ink/80">
              {CINTA_LANDING.authority.map((line) => (
                <li key={line} className="flex items-start justify-center gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-lunins-accent" />
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Depoimentos completos */}
        <section className="border-t border-lunins-border/60 bg-lunins-sand/50 py-14">
          <div className="mx-auto max-w-6xl px-4">
            <div className="text-center">
              <h2 className="font-display text-2xl font-bold text-lunins-ink sm:text-3xl">{CINTA_LANDING.testimonials.headline}</h2>
              <p className="mt-2 text-lunins-muted">{CINTA_LANDING.testimonials.subtitle}</p>
            </div>
            <div className="mt-10 grid gap-6 lg:grid-cols-2">
              {CINTA_LANDING.testimonials.items.map((t) => (
                <div key={t.video} className="group overflow-hidden rounded-2xl border border-lunins-border bg-lunins-surface shadow-card transition-colors hover:border-lunins-accent/30">
                  <video
                    className="aspect-video w-full bg-black"
                    controls
                    playsInline
                    preload="metadata"
                  >
                    <source src={t.video} type="video/mp4" />
                  </video>
                  <div className="p-5">
                    <p className="text-sm italic leading-relaxed text-lunins-ink/80">
                      <Quote className="mr-1 inline h-3.5 w-3.5 -translate-y-0.5 text-lunins-accent/60" />
                      {t.highlight}
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-lunins-accent/10">
                        <User className="h-4 w-4 text-lunins-accent" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-lunins-ink">{t.name}</div>
                        <div className="flex items-center gap-1 text-xs text-lunins-muted">
                          <MapPin className="h-3 w-3" />
                          {t.location} · {t.usagePeriod}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14">
          <div className="mx-auto max-w-3xl px-4">
            <h2 className="font-display text-2xl font-bold text-lunins-ink">Perguntas frequentes</h2>
            <div className="mt-8 space-y-4">
              {CINTA_LANDING.faq.map((item) => (
                <details
                  key={item.q}
                  className="group rounded-2xl border border-lunins-border bg-lunins-surface/60 p-4 open:border-lunins-accent/30"
                >
                  <summary className="cursor-pointer list-none font-semibold text-lunins-ink marker:content-none">
                    {item.q}
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-lunins-muted">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Checkout */}
        <section id="checkout" className="scroll-mt-20 border-t border-lunins-accent/20 bg-lunins-sand py-14">
          <div className="mx-auto max-w-lg px-4">
            <h2 className="font-display text-2xl font-bold text-lunins-ink">Finalize seu pedido</h2>
            <p className="mt-2 text-sm text-lunins-muted">{CINTA_LANDING.urgency}</p>
            <p className="mt-2 text-xs text-lunins-muted">
              Pagamento seguro via Mercado Pago com Compra Garantida — seu dinheiro fica protegido até você receber.
            </p>

            <div className="mt-6 flex gap-2">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className={`h-1.5 flex-1 rounded-full ${step >= n ? 'bg-lunins-accent' : 'bg-lunins-border'}`}
                />
              ))}
            </div>

            {error && (
              <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {error}
              </div>
            )}

            <div className="mt-6 rounded-2xl border border-lunins-border bg-lunins-surface p-5 shadow-card">
              {step === 1 && (
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-lunins-ink">Etapa 1 — Seus dados</p>
                  <label className="block">
                    <span className="text-xs text-lunins-muted">Nome completo</span>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                      placeholder="Como consta no documento"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-lunins-muted">WhatsApp</span>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneDisplay(e.target.value))}
                      className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                      placeholder="(67) 99999-9999"
                      inputMode="tel"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-lunins-muted">E-mail (opcional)</span>
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                      placeholder="para NF-e"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={loading || name.trim().length < 2 || normalizePhoneDigits(phone).length < 10}
                    onClick={() => saveStep(2)}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-lunins-ink py-3 text-sm font-semibold text-lunins-cream disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Continuar
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-lunins-ink">Etapa 2 — Dados para NF-e e entrega</p>
                  <label className="block">
                    <span className="text-xs text-lunins-muted">CPF ou CNPJ</span>
                    <input
                      value={cpfCnpj}
                      onChange={(e) => setCpfCnpj(formatCpfCnpj(e.target.value))}
                      className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-lunins-muted">Nome / razão social (NF-e)</span>
                    <input
                      value={razaoSocial}
                      onChange={(e) => setRazaoSocial(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-lunins-muted">CEP</span>
                    <div className="relative">
                      <input
                        value={cep}
                        onChange={(e) => setCep(formatCep(e.target.value))}
                        onBlur={() => void handleCepBlur()}
                        className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                        placeholder="00000-000"
                      />
                      {cepLoading && (
                        <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-lunins-accent" />
                      )}
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-xs text-lunins-muted">Endereço</span>
                    <input
                      value={logradouro}
                      onChange={(e) => setLogradouro(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                    />
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <label className="col-span-1 block">
                      <span className="text-xs text-lunins-muted">Nº</span>
                      <input
                        value={numero}
                        onChange={(e) => setNumero(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                      />
                    </label>
                    <label className="col-span-2 block">
                      <span className="text-xs text-lunins-muted">Bairro</span>
                      <input
                        value={bairro}
                        onChange={(e) => setBairro(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <label className="col-span-2 block">
                      <span className="text-xs text-lunins-muted">Cidade</span>
                      <input
                        value={municipio}
                        onChange={(e) => setMunicipio(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-lunins-muted">UF</span>
                      <input
                        value={uf}
                        onChange={(e) => setUf(e.target.value.toUpperCase().slice(0, 2))}
                        className="mt-1 w-full rounded-xl border border-lunins-border bg-lunins-cream px-3 py-2.5 text-sm text-lunins-ink outline-none focus:border-lunins-accent"
                        maxLength={2}
                      />
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="rounded-full border border-lunins-border px-4 py-2 text-sm text-lunins-ink/80"
                    >
                      Voltar
                    </button>
                    <button
                      type="button"
                      disabled={
                        loading ||
                        cpfCnpj.replace(/\D/g, '').length < 11 ||
                        !logradouro.trim() ||
                        !numero.trim() ||
                        cep.replace(/\D/g, '').length !== 8
                      }
                      onClick={() => saveStep(3)}
                      className="flex flex-1 items-center justify-center gap-2 rounded-full bg-lunins-ink py-3 text-sm font-semibold text-lunins-cream disabled:opacity-50"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Revisar pedido
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <p className="text-sm font-semibold text-lunins-ink">Etapa 3 — Pagamento seguro</p>
                  <div className="rounded-xl border border-lunins-border/80 bg-lunins-cream/60 p-4 text-sm">
                    <div className="flex justify-between text-lunins-ink/80">
                      <span>{product?.name ?? 'Cinta Modeladora'}</span>
                      <span className="font-semibold text-lunins-ink">{installmentLabel}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-lunins-muted">
                      <Truck className="h-3.5 w-3.5" />
                      Frete grátis · NF-e após pagamento
                    </div>
                    <div className="mt-3 border-t border-lunins-border pt-3 text-xs text-lunins-muted">
                      <div>{name}</div>
                      <div>{phone}</div>
                      <div>
                        {logradouro}, {numero} — {bairro}
                      </div>
                      <div>
                        {municipio}/{uf} · CEP {cep}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="rounded-full border border-lunins-border px-4 py-2 text-sm text-lunins-ink/80"
                    >
                      Voltar
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => void handleCheckout()}
                      className="flex flex-1 items-center justify-center gap-2 rounded-full bg-lunins-ink py-3 text-sm font-semibold text-lunins-cream disabled:opacity-50"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Pagar com Mercado Pago
                    </button>
                  </div>
                </div>
              )}
            </div>

            <p className="mt-4 text-center text-xs text-lunins-muted">
              Prefere falar com alguém?{' '}
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="text-lunins-accent underline">
                WhatsApp Lunins
              </a>
            </p>
          </div>
        </section>

        <BrandTrustStrip />
      </main>

      <SiteFooter />

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-lunins-border/80 bg-lunins-cream/95 p-3 backdrop-blur md:hidden">
        <button
          type="button"
          onClick={() => handleCta('sticky')}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-lunins-ink py-3.5 text-sm font-semibold text-lunins-cream"
        >
          {CINTA_LANDING.hero.cta} · {installmentLabel}
        </button>
      </div>
    </div>
  );
}
